import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/context/AuthContext";
import { Auth } from "@/lib/api";
import { Loader2, LogOut, PlayCircle, FileText, ShoppingCart, MessagesSquare, Send, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "/atc-logo.png";

const NAV = [
  { to: "/portal", label: "Training Videos", icon: PlayCircle, end: true, testId: "portal-nav-videos" },
  { to: "/portal/manuals", label: "Manuals & PDFs", icon: FileText, testId: "portal-nav-manuals" },
  { to: "/portal/reorder", label: "Order Equipment", icon: ShoppingCart, testId: "portal-nav-reorder" },
  { to: "/portal/qa", label: "Q & A", icon: MessagesSquare, testId: "portal-nav-qa" },
  { to: "/portal/contact", label: "Contact ATC", icon: Send, testId: "portal-nav-contact" },
];

const PortalLayout = () => {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    // If the user isn't loading and lacks portal access, bounce them.
    if (!loading && user && !user.is_portal_member && !user.is_admin) {
      navigate("/portal/login?denied=1", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-atc-bg">
        <Loader2 className="h-6 w-6 animate-spin text-atc-blue" />
      </main>
    );
  }
  if (!user) return <Navigate to="/portal/login" replace />;

  const signOut = async () => {
    setSigningOut(true);
    try {
      await Auth.logout();
      setUser(null);
      navigate("/portal/login", { replace: true });
    } catch { toast.error("Sign out failed"); }
    finally { setSigningOut(false); }
  };

  return (
    <main className="min-h-screen bg-atc-bg text-slate-100 flex" data-testid="portal-layout">
      <Helmet>
        <meta name="robots" content="noindex, nofollow, noarchive" />
        <title>Dealer Portal · ATC Mold & Water</title>
      </Helmet>
      <aside className="w-64 shrink-0 border-r border-white/5 bg-slate-950/70 backdrop-blur-xl flex flex-col">
        <div className="px-5 py-6 border-b border-white/5">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="ATC" className="h-9 w-9 object-contain" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-atc-blue">ATC Portal</div>
              <div className="text-sm text-white font-semibold">Dealer &amp; Customer</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                  isActive
                    ? "bg-atc-blue/15 border border-atc-blue/30 text-white"
                    : "text-slate-300 hover:text-white hover:bg-white/5"
                }`
              }
              data-testid={testId}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/5 space-y-1.5">
          {user.is_admin && (
            <Link to="/admin" className="flex items-center gap-2 px-3 py-2 text-xs text-atc-green hover:text-white" data-testid="portal-admin-link">
              Open Admin Dashboard →
            </Link>
          )}
          <Link to="/" className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-white" data-testid="portal-back-site">
            <ChevronLeft className="h-3.5 w-3.5" /> Public site
          </Link>
          <div className="flex items-center gap-2 px-3 py-2">
            {user.picture ? (
              <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
            ) : (
              <div className="h-7 w-7 rounded-full bg-atc-blue/20 border border-atc-blue/40" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white truncate">{user.name || user.email}</div>
              <div className="text-[10px] text-slate-500 truncate">{user.email}</div>
            </div>
          </div>
          <button
            onClick={signOut}
            disabled={signingOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
            data-testid="portal-signout"
          >
            <LogOut className="h-3.5 w-3.5" /> {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </main>
  );
};

export default PortalLayout;
